// TODO REFACTOR THIS FILE ESPECIALLT THE ADVANCE ACTION EXECUTION

import React, { useCallback } from 'react'

import { PluginFieldType } from '@makyoapp/makyo-sdk/models/Plugins'
import { ITableRow } from '@makyoapp/makyo-sdk/models/Table.js'
import {
  ConditionFeatureFlag,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  IActionRepeaterValue,
  IActionSettings,
  IActionType,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'

import { globalVariableClient } from '@/core/app-builder/global-variables'
import { parseConditionBasedOnOperator } from '@/core/app-builder/parser/condition'
import { parseProfileData } from '@/core/app-builder/parser/profile-data'
import { parseSingleCellValue } from '@/core/app-builder/parser/single-cell'
import {
  parseFilterDataAsync,
  parsePageContent,
  parsePageTitle,
  parseVariableTextActionOutput,
} from '@/core/app-builder/parser/variable-text'
import { FilterConfig } from '@/core/db/operations/filter'
import cookies from '@/lib/cookies'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { ActionExternalData, ActionHandlers, IActionSequence } from '@/shared/action.types'

import { useActiveLayerContext } from '../ActiveLayerContext'
import { useAppAuthContext } from '../AppAuthContext'
import { useAppClientContext } from '../AppClientContext'
import { useDbClientContext } from '../DbClientContext'

// Helper to collect all reachable actions from a given nextAction
function collectReachableActions(
  start: string | { actionId: string }[] | undefined,
  actionMap: Map<string, IActionRepeaterValue>
): IActionRepeaterValue[] {
  const result: IActionRepeaterValue[] = []
  const visited = new Set<string>()
  const queue: string[] = []

  // Seed the queue
  if (Array.isArray(start)) {
    for (const step of start) {
      if (typeof step === 'string') {
        queue.push(step)
      } else if (step && typeof step === 'object' && 'actionId' in step) {
        queue.push(step.actionId)
      }
    }
  } else if (typeof start === 'string') {
    queue.push(start)
  }

  // Traverse
  while (queue.length > 0) {
    const nextId = queue.shift()!
    if (!visited.has(nextId)) {
      visited.add(nextId)
      const nextAct = actionMap.get(nextId)
      if (nextAct) {
        result.push(nextAct)
        if (nextAct.nextAction) {
          if (Array.isArray(nextAct.nextAction)) {
            for (const step of nextAct.nextAction) {
              if (typeof step === 'string') {
                queue.push(step)
              } else if (step && typeof step === 'object' && 'actionId' in step) {
                queue.push(step.actionId)
              }
            }
          } else if (typeof nextAct.nextAction === 'string') {
            queue.push(nextAct.nextAction)
          }
        }
      }
    }
  }
  return result
}

/**
 * New action executor hook for advanced action workflow
 *
 * @todo
 * - better naming
 * - exctract utils/dedicated function into a separate file
 */
export function useSimulateActionV2(
  actionHandlers: ActionHandlers,
  currentOrgId: string,
  parseActionSettingsData: (settings: IActionSettings, sequenceId: string) => any,
  actionSequenceMapRef: React.MutableRefObject<Map<string, IActionSequence>>
) {
  // Access global contexts
  const { appClient } = useAppClientContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { project } = useAppBuilderContext()
  const { activePageClient } = useAppPagesContext()
  const { activeLayerClient } = useActiveLayerContext()
  const { userEmail } = useAppAuthContext()

  // Resolves condition value using the same logic as useCheckVisibilityV2
  const resolveConditionValue = useCallback(
    async (condition: any, isEnd: boolean, tableId?: string, rowId?: string, external?: ActionExternalData) => {
      if (!dbClient) return null
      switch (condition.flag) {
        case ConditionFeatureFlag.CurrentRow: {
          const { col, endCol } = condition
          let colId = isEnd ? endCol : col
          const resolvedTableId = external?.detailsPage?.detailTableId || tableId
          const dbTable = dbClient.tablesMap.get(resolvedTableId!)
          if (!dbTable) return null
          const rowIndex = rowId ? dbTable.GetRowIndexByRowId(rowId, language, userSpecific) : -1

          const value = parseSingleCellValue(
            { tbl: resolvedTableId, col: colId, row: rowIndex },
            dbClient,
            language,
            userSpecific,
            false
          )
          return value
        }
        case ConditionFeatureFlag.ColumnData: {
          const { tbl, endTbl, col, endCol } = condition
          let tblId = isEnd ? endTbl : tbl
          let colId = isEnd ? endCol : col

          const dbTable = dbClient.tablesMap.get(tblId!)
          if (!dbTable) return null
          const rowIdx = rowId ? dbTable.GetRowIndexByRowId(rowId, language, userSpecific) : -1

          const value = parseSingleCellValue(
            { tbl: tblId, col: colId, row: String(rowIdx) },
            dbClient,
            language,
            userSpecific,
            false
          )
          return value?.toString()
        }
        case ConditionFeatureFlag.SingleCell: {
          const { tbl, endTbl, col, endCol, row, endRow } = condition
          let tblId = isEnd ? endTbl : tbl
          let colId = isEnd ? endCol : col
          let rowIndex = isEnd ? endRow : row

          const value = parseSingleCellValue(
            {
              tbl: tblId,
              col: colId,
              row: String(Number(rowIndex) - 1),
            },
            dbClient,
            language,
            userSpecific,
            false
          )

          return value?.toString()
        }
        // case ConditionFeatureFlag.CustomText: {
        //   const { tbl, endTbl, col, endCol, row, endRow } = condition
        //   let tblId = isEnd ? endTbl : tbl
        //   let colId = isEnd ? endCol : col
        //   let rowIndex = isEnd ? endRow : row
        //   const value = parseSingleCellValueImpl(
        //     { tbl: tblId, col: colId, row: String(Number(rowIndex) - 1) },
        //     dbClient,
        //     language,
        //     userSpecific,
        //     false
        //   )
        //   return value?.toString()
        // }
        case ConditionFeatureFlag.SessionId: {
          return cookies.getAppSessionId(window.appID)
        }
        case ConditionFeatureFlag.PageContent: {
          const pageClient = activePageClient
          if (!pageClient) return condition.value
          switch ((condition?.value ?? '').toLowerCase()) {
            case 'title':
              return parsePageContent(pageClient, language, userSpecific, '_Label', rowId)
            case 'description':
              return parsePageContent(pageClient, language, userSpecific, '_Description', rowId)
            case 'name':
              return parsePageTitle(pageClient, language, userSpecific, rowId)
            case 'sub title':
              return parsePageContent(pageClient, language, userSpecific, '_Subtitle', rowId)
            default:
              return condition.value
          }
        }
        case ConditionFeatureFlag.CustomText:
          return isEnd ? condition.endValue : condition.value
        case ConditionFeatureFlag.UserLoggedIn: {
          return !!userEmail
        }
        case ConditionFeatureFlag.ProfileData: {
          const profileDataValue = parseProfileData(condition.col, dbClient, userEmail)
          return profileDataValue
        }
        case ConditionFeatureFlag.FormData: {
          const { col } = condition
          return external?.formState?.[col]
        }
        case ConditionFeatureFlag.DateTime: {
          const { value, endValue } = condition
          const dateVal = isEnd ? endValue : value
          return dateVal
        }
        case ConditionFeatureFlag.ActionOutput: {
          // const isFilterData = condition.actionType === IActionType.FilterData
          const actionOutput = external?.allActions?.find((action) => action.id === condition.actionId)

          if (actionOutput) {
            switch (actionOutput.type) {
              case IActionType.TemporaryVariable:
                if (!activeLayerClient) return null
                return await parseVariableTextActionOutput(
                  actionOutput.settings.value,
                  dbClient,
                  activeLayerClient,
                  userEmail,
                  language,
                  userSpecific,
                  tableId,
                  rowId,
                  external?.allActions ?? []
                )
              case IActionType.FilterData:
                const rowValue = await parseFilterDataAsync(
                  actionOutput.id,
                  external?.allActions ?? [],
                  dbClient as any,
                  activeLayerClient,
                  userEmail,
                  language,
                  userSpecific,
                  {
                    detailsPage: {
                      detailTableId: external?.detailsPage?.detailTableId ?? tableId,
                      detailRowId: external?.detailsPage?.detailRowId ?? rowId,
                    },
                  }
                )
                if (rowValue) {
                  const chosenColId = condition.value.colId ?? ''

                  if (Array.isArray(rowValue)) {
                    return rowValue.map((item) => item[chosenColId] ?? '')?.join('') ?? ''
                  } else if (typeof rowValue === 'object') {
                    return rowValue[chosenColId]?.value ?? ''
                  } else {
                    return rowValue ?? ''
                  }
                }
                return rowValue
              default:
                return null
            }
          }
          return null
        }
        case ConditionFeatureFlag.GlobalVariable: {
          const varName = condition.value
          const value = globalVariableClient?.getValue(varName)
          return value ?? ''
        }
        default:
          return null
      }
    },
    [dbClient, language, userSpecific, activePageClient, userEmail, activeLayerClient]
  )

  // Evaluates a condition using the same logic as useCheckVisibilityV2
  const evaluateCondition = useCallback(
    async (condition: any, sequenceId: string) => {
      const actionSequence = actionSequenceMapRef.current.get(sequenceId)!
      const { triggerSource, external } = actionSequence

      const tableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
      const rowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

      if (!condition || !dbClient) return true
      const { isAny = true, conditions = [] } = condition
      if (conditions.length === 0) return true
      try {
        const conditionResults = await Promise.all(
          conditions.map(async (cond: any) => {
            const rangeFields = [...NUMERIC_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS]
            const isRange = rangeFields.includes(cond.operator)
            const lhsValue = await resolveConditionValue(cond.lhs, false, tableId, rowId, external)
            let rhsValue, rhsEndValue
            if (cond.rhs) {
              rhsValue = await resolveConditionValue(cond.rhs, false, tableId, rowId, external)
              if (isRange) {
                rhsEndValue = await resolveConditionValue(cond.rhsEnd, true, tableId, rowId, external)
              }
            }

            const rhsParam = !isRange ? { value: rhsValue } : { start: rhsValue, end: rhsEndValue }
            const conditionResult = parseConditionBasedOnOperator(lhsValue, cond.operator, rhsParam, true)
            return conditionResult
          })
        )
        return isAny ? conditionResults.some((r: boolean) => r) : conditionResults.every((r: boolean) => r)
      } catch (error) {
        console.error('Error evaluating condition:', error)
        return false
      }
    },
    [actionSequenceMapRef, dbClient, resolveConditionValue]
  )

  // Execute plugin action
  const executePluginAction = useCallback(
    async (action: IActionRepeaterValue, sequenceId: string) => {
      if (!appClient || !currentOrgId || !project) return

      const actionSequence = actionSequenceMapRef.current.get(sequenceId)
      if (!actionSequence) return

      const { pluginAction, ...fields } = action.settings
      const credentialKey = pluginAction.fields.find((field: any) => field._Type === PluginFieldType.Credentials)?._Key
      const credentialValue = fields[credentialKey]
      const theFields = pluginAction.fields.filter((field: any) => field._Type !== PluginFieldType.Credentials)

      // parse subject with encoding, to support special characters such as emojis
      const parseSubjectWithEncoding = (value: string) => {
        try {
          const base64EncodedSubject = btoa(unescape(encodeURIComponent(value)))
          return `=?UTF-8?B?${base64EncodedSubject}?=`
        } catch (e) {
          return value // fallback to unencoded
        }
      }

      const transformFields = async (fields: Record<string, any>) => {
        const params: Record<string, any> = {}

        for (const field of theFields) {
          const key = field._Key
          const fieldType = field._Type as PluginFieldType
          const isRequired = fieldType !== PluginFieldType.Boolean && field._Optional === false

          const parsedValue = await parseActionSettingsData(fields[key], sequenceId)

          switch (field._Type) {
            case PluginFieldType.Chips:
              if (parsedValue) {
                params[key] = parsedValue.split(',')
              }
              break
            case PluginFieldType.Boolean:
              params[key] = !!fields[key]
              break
            default:
              params[key] = parsedValue
          }

          if (key === 'subject' && parsedValue) {
            params[key] = parseSubjectWithEncoding(parsedValue)
          }
        }
        return params
      }

      let params = await transformFields(fields)

      const { triggerSource } = actionSequence

      const generateWorkflowName = () => {
        // return `[${triggerSource?.type}]-[${triggerSource?.label}]-[${action.type}]`
        return `${action.type} (${triggerSource?.label} ${triggerSource?.type})`
      }

      // additional params for error logging
      params._project_id = project.id
      params._project_name = project.title
      params._workflow = generateWorkflowName()
      params._url = window.location.href

      await appClient.provider.executePluginAction(currentOrgId, credentialValue, {
        _Action: pluginAction.actionKey,
        _Params: params,
      })
    },
    [actionSequenceMapRef, appClient, currentOrgId, parseActionSettingsData, project]
  )

  // Executes a single action with the given options
  const executeSingleAction = useCallback(
    async (action: IActionRepeaterValue, sequenceId: string, allActions?: IActionRepeaterValue[]): Promise<void> => {
      const { settings, type: actionType } = action
      const handler = actionHandlers[actionType as keyof ActionHandlers]

      // Handle plugin
      if (action.settings.pluginAction && currentOrgId) {
        await executePluginAction(action, sequenceId)
      }

      if (!handler) {
        console.warn(`No handler found for action type: ${actionType}`)
        return
      }

      // Handle ShowDialog - store remaining actions in queue
      if (actionType === IActionType.ShowDialog) {
        let nextActions: IActionRepeaterValue[] = []
        if (allActions && action.nextAction) {
          const actionMap = new Map(allActions.map((a) => [a.id, a]))
          nextActions = collectReachableActions(action.nextAction, actionMap)
        }

        actionSequenceMapRef.current.get(sequenceId)!.queue = [...nextActions]
      }

      await handler({ sequenceId, actionId: action.id, settings })

      //
    },
    [actionHandlers, actionSequenceMapRef, currentOrgId, executePluginAction]
  )

  // Handle AddToCart action logic
  const handleAddToCart = useCallback(
    async (settings: any, sequenceId: string) => {
      const actionSequence = actionSequenceMapRef.current.get(sequenceId)!
      const { triggerSource, external } = actionSequence

      const defaultTableId = triggerSource?.tableId ?? external?.detailsPage?.detailTableId
      const defaultRowId = triggerSource?.rowId ?? external?.detailsPage?.detailRowId

      if (!dbClient) {
        console.warn('DbClient not available for AddToCart action')
        return
      }

      try {
        const {
          productTableId,
          productIdColumnId,
          cartTableId,
          cartProductIdColumnId,
          cartQuantityColumnId,
          cartSessionIdColumnId,
        } = settings

        if (
          !productTableId ||
          !productIdColumnId ||
          !cartTableId ||
          !cartProductIdColumnId ||
          !cartQuantityColumnId ||
          !cartSessionIdColumnId
        ) {
          console.warn('AddToCart action: Missing required table/column configuration')
          return
        }

        // Get current session ID
        const sessionId = cookies.getAppSessionId(window.appID)
        if (!sessionId) {
          console.warn('AddToCart action: No session ID available')
          return
        }

        const tableId = productTableId
        const rowId = defaultRowId

        const dbTable = dbClient.tablesMap.get(tableId!)
        if (!dbTable) return
        const rowIdx = rowId ? dbTable.GetRowIndexByRowId(rowId, language, userSpecific) : -1

        // Get product ID from current row
        const productId = parseSingleCellValue(
          { tbl: tableId, col: productIdColumnId, row: String(rowIdx) },
          dbClient,
          language,
          userSpecific,
          false
        )

        if (!productId) {
          console.warn('AddToCart action: No product ID found')
          return
        }

        // Get cart table
        const cartTable = dbClient.tablesMap.get(cartTableId)
        if (!cartTable) {
          console.warn('AddToCart action: Could not get cart table')
          return
        }

        // Construct filter conditions to find existing cart item
        const filterConditions = [
          {
            lhs: cartProductIdColumnId, // Column ID as string
            operator: ConditionTextOperator.IsEqual,
            rhs: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: productId,
            },
            caseSensitive: false,
            userSpecific: false,
          },
          {
            lhs: cartSessionIdColumnId, // Column ID as string
            operator: ConditionTextOperator.IsEqual,
            rhs: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: sessionId,
            },
            caseSensitive: false,
            userSpecific: false,
          },
        ]

        const filterConfig: FilterConfig = {
          settings: {
            conditions: filterConditions as any, // Cast to bypass type issues
            isAny: false, // AND operation - both conditions must be true
          },
          userEmail: userEmail || '',
          tableId: cartTableId,
        }

        // Query for existing cart item with filter
        const filteredResult = await cartTable.GetFormattedData(language, userSpecific, undefined, filterConfig)
        const existingCartItem = filteredResult.rows.length > 0 ? filteredResult.rows[0] : null
        if (existingCartItem) {
          // Get current rows
          const currentRows = cartTable.GetDbWorkerRows(language, userSpecific)

          // Format the payload (rows to update)
          let payload = await Promise.all(
            [existingCartItem._id].map(async (rowId) => {
              let rowIdx = currentRows.findIndex((r: any) => r._id === rowId)
              let rowData = currentRows[rowIdx]
              if (!rowData) return null

              // Product exists in cart - increment quantity
              const currentQuantity = Number(rowData[cartQuantityColumnId] || 0)
              const newQuantity = currentQuantity + 1

              return {
                ...rowData,
                [cartQuantityColumnId]: newQuantity,
              }
            })
          )

          // Filter out any null values and cast to ITableRow[]
          payload = payload.filter(Boolean) as any[]

          // Update dbTable data
          await cartTable.UpdateData(payload as ITableRow[], language, userSpecific)
          return {
            isInCart: true,
          }
        } else {
          // Product doesn't exist in cart - create new row
          const newCartRow = {
            [cartProductIdColumnId]: productId,
            [cartQuantityColumnId]: 1,
            [cartSessionIdColumnId]: sessionId,
          }

          await cartTable.AddRow(newCartRow as ITableRow, language)
          return {
            isInCart: false,
          }
        }
      } catch (error) {
        console.error('AddToCart action error:', error)
      }
    },
    [actionSequenceMapRef, dbClient, language, userEmail, userSpecific]
  )

  // Executes advanced actions in a graph-like structure based on nextAction relationships
  const executeAdvancedActions = useCallback(
    async (sequenceId: string, actions: IActionRepeaterValue[], startId: string): Promise<void> => {
      const visited = new Set<string>()
      const actionMap = new Map(actions.map((a) => [a.id, a]))
      const NON_HANDLER_ACTIONS = [
        IActionType.ConditionalIf,
        IActionType.ConditionalSwitch,
        IActionType.FilterData,
        IActionType.AddToCart,
      ]

      const run = async (id: string): Promise<void> => {
        // Prevent infinite loops by tracking visited nodes
        if (visited.has(id)) {
          console.warn(`Circular dependency detected for action ${id}`)
          return
        }
        visited.add(id)

        const action = actionMap.get(id)
        if (!action) {
          console.warn(`Action with id ${id} not found`)
          return
        }

        if (!NON_HANDLER_ACTIONS.includes(action.type)) {
          try {
            await executeSingleAction(action, sequenceId, actions)
          } catch (err) {
            // sonner.show({ description: 'Ooops...something went wrong!', bgColor: 'neutral-0' })
            return Promise.reject(err)
          }
        }

        // Follow the nextAction link if it exists
        if (action.nextAction) {
          if (typeof action.nextAction === 'string') {
            // Simple string reference to next action
            await run(action.nextAction)
            //
          } else if (Array.isArray(action.nextAction)) {
            // Handle ConditionalSwitch vs ConditionalIf differently
            switch (action.type) {
              case IActionType.ConditionalSwitch:
                // For Switch actions: evaluate all conditions and run ALL matching branches concurrently
                const matchingActions: string[] = []

                for (const cond of action.nextAction) {
                  const conditionResult = await evaluateCondition(cond.condition, sequenceId)
                  if (conditionResult) {
                    matchingActions.push(cond.actionId)
                  }
                }

                // Execute all matching actions concurrently
                if (matchingActions.length > 0) {
                  await Promise.all(matchingActions.map((actionId) => run(actionId)))
                }
                break

              case IActionType.ConditionalIf:
                // For ConditionalIf: run the first matching branch only
                let nextAction = null
                for (const cond of action.nextAction) {
                  const conditionResult = await evaluateCondition(cond.condition, sequenceId)
                  if (conditionResult) {
                    nextAction = cond
                    break
                  }
                }

                if (nextAction) {
                  await run(nextAction.actionId)
                }
                break

              case IActionType.AddToCart:
                const result = await handleAddToCart(action.settings, sequenceId)

                if (result && Object.hasOwn(result, 'isInCart')) {
                  if (result.isInCart) {
                    await run(action.nextAction.find((cond) => cond.default === false)?.actionId || '')
                  } else {
                    await run(action.nextAction.find((cond) => cond.default === true)?.actionId || '')
                  }
                }
                break

              default:
                // For other conditional actions: run the first matching branch only
                let defaultNextAction = null
                for (const cond of action.nextAction) {
                  const conditionResult = await evaluateCondition(cond.condition, sequenceId)
                  if (conditionResult) {
                    defaultNextAction = cond
                    break
                  }
                }

                if (defaultNextAction) {
                  await run(defaultNextAction.actionId)
                }
                break
            }
          }
        }
      }

      await run(startId)
    },
    [evaluateCondition, executeSingleAction, handleAddToCart]
  )

  return { executeAdvancedActions, executePluginAction }
}
